import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Input,
  Select,
  Space,
  Tag,
  Tooltip,
  Button,
  message,
  Typography,
  Modal,
  Form,
  Popconfirm,
} from 'antd';
import {
  ReloadOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  EditOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import {
  makeGetCall,
  makePostCall,
  makePutCall,
  getUserDetails,
} from '../../utils/helper';
import moment from 'moment';

const { Option } = Select;

interface EnrolledUsersListProps {
  // When `program` is passed, the list is scoped to that program: no program
  // dropdown, no program column, header reflects the program. This is how the
  // page is consumed under Program Management → row click. The unscoped form
  // is kept for backwards compatibility.
  program?: any;
  onBack?: () => void;
}

const EnrolledUsersList: React.FC<EnrolledUsersListProps> = ({ program, onBack }) => {
  const scoped = !!program;
  const [users, setUsers] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [programFilter, setProgramFilter] = useState<number | undefined>(
    program?.program_id
  );
  const [search, setSearch] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const effectiveProgramId = scoped ? program.program_id : programFilter;
      if (effectiveProgramId !== undefined) {
        params.append('programId', String(effectiveProgramId));
      }
      if (search.trim()) params.append('search', search.trim());
      const qs = params.toString();
      const response = await makeGetCall(`/enrolled-user/list${qs ? `?${qs}` : ''}`);
      setUsers(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch enrolled users');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchPrograms = async () => {
    try {
      const response = await makeGetCall('/program/list');
      setPrograms(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch programs', error);
    }
  };

  // Programs are needed in both views: the filter (unscoped) and the
  // add/edit modal's program picker.
  useEffect(() => {
    fetchPrograms();
  }, []);

  // Debounce search; refetch immediately on program filter change
  useEffect(() => {
    const t = setTimeout(fetchUsers, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, programFilter, program?.program_id]);

  const programById = useMemo(() => {
    const map = new Map<number, any>();
    programs.forEach((p) => map.set(p.program_id, p));
    return map;
  }, [programs]);

  const renderProgramCell = (record: any) => {
    if (!record.enrolled_user_program_id) {
      return <span style={{ color: '#999' }}>—</span>;
    }
    const fromInclude = record.program;
    const fromList = programById.get(record.enrolled_user_program_id);
    const name =
      fromInclude?.program_name ||
      fromList?.program_name ||
      `Program #${record.enrolled_user_program_id}`;
    const isActive =
      fromInclude?.program_is_active ?? fromList?.program_is_active ?? true;
    return (
      <Space size={6}>
        <span>{name}</span>
        {!isActive && <Tag color="default">Inactive</Tag>}
      </Space>
    );
  };

  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ programId: scoped ? program.program_id : programFilter });
    setIsModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingUser(record);
    form.resetFields();
    form.setFieldsValue({
      email: record.enrolled_user_email,
      name: record.enrolled_user_name,
      programId: record.enrolled_user_program_id ?? undefined,
    });
    setIsModalVisible(true);
  };

  const handleUnenroll = async (record: any) => {
    try {
      await makePutCall(`/enrolled-user/${record.enrolled_user_id}/unenroll`, {});
      message.success('Participant unenrolled');
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to unenroll participant');
      console.error(error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload: any = {
        email: values.email,
        name: values.name || null,
        programId: values.programId ?? null,
      };
      // On edit, a blank password means "keep the current one".
      if (values.password) payload.password = values.password;

      if (editingUser) {
        await makePutCall(`/enrolled-user/${editingUser.enrolled_user_id}`, payload);
        message.success('Participant updated');
      } else {
        payload.createdBy = getUserDetails()?.username || 'manual';
        await makePostCall('/enrolled-user/create', payload);
        message.success('Participant added');
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchUsers();
    } catch (error) {
      if (error.errorFields) return;
      message.error(error.response?.data?.message || 'Failed to save participant');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const columns: any = [
    {
      title: 'ID',
      dataIndex: 'enrolled_user_id',
      key: 'enrolled_user_id',
      width: 80,
      render: (id: number) => (
        <Typography.Text strong style={{ fontFamily: 'monospace' }}>
          {id}
        </Typography.Text>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'enrolled_user_email',
      key: 'enrolled_user_email',
      width: '25%',
      render: (email: string) => (
        <Typography.Text copyable={{ text: email }}>{email}</Typography.Text>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'enrolled_user_name',
      key: 'enrolled_user_name',
      width: '20%',
      render: (name: string) =>
        name || <span style={{ color: '#999' }}>(unnamed)</span>,
    },
    // Program column is only useful in the global (unscoped) view.
    ...(!scoped ? [{
      title: 'Current Program',
      key: 'program',
      width: '25%',
      render: (_: any, record: any) => renderProgramCell(record),
    }] : []),
    {
      title: 'Last Login',
      dataIndex: 'enrolled_user_last_login',
      key: 'enrolled_user_last_login',
      width: 170,
      render: (val: string) =>
        val ? (
          <Tooltip title={moment(val).format('DD MMM YYYY, HH:mm:ss')}>
            {moment(val).fromNow()}
          </Tooltip>
        ) : (
          <span style={{ color: '#999' }}>Never</span>
        ),
    },
    {
      title: 'Enrolled On',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (val: string) =>
        val ? moment(val).format('DD MMM YYYY') : <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Source',
      dataIndex: 'enrolled_user_created_by',
      key: 'enrolled_user_created_by',
      width: 160,
      render: (val: string) =>
        val || <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      fixed: 'right',
      render: (_: any, record: any) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          {record.enrolled_user_program_id ? (
            <Popconfirm
              title="Unenroll this participant from their program? Their login and past responses are kept."
              onConfirm={() => handleUnenroll(record)}
              okText="Unenroll"
              cancelText="Cancel"
            >
              <Tooltip title="Unenroll">
                <Button type="link" danger icon={<UserDeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          ) : (
            <Tooltip title="Not enrolled in any program">
              <Button type="link" danger disabled icon={<UserDeleteOutlined />} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const heading = scoped
    ? (program.program_name || `Program #${program.program_id}`)
    : 'Enrolled Users';
  const subheading = scoped
    ? `Participants enrolled in this program, via the program-enroller integration or added manually.`
    : `Participants provisioned by the program-enroller integration or added manually.`;

  return (
    <div className="p-4">
      {scoped && onBack && (
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          className="mb-2"
        >
          Back to Programs
        </Button>
      )}
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">
            {scoped ? `${heading} — Enrolled Users` : heading}
          </h2>
          <p className="text-gray-500">{subheading}</p>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchUsers}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add Participant
          </Button>
        </Space>
      </div>

      <div className="mb-3 flex gap-2 items-center">
        <Input.Search
          allowClear
          placeholder="Search by email or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 320 }}
        />
        {!scoped && (
          <Select
            allowClear
            placeholder="Filter by program"
            value={programFilter}
            onChange={(v) => setProgramFilter(v)}
            style={{ width: 280 }}
            showSearch
            optionFilterProp="children"
          >
            {programs.map((p) => (
              <Option key={p.program_id} value={p.program_id}>
                {p.program_name || `Program #${p.program_id}`}
              </Option>
            ))}
          </Select>
        )}
        <span className="text-gray-500" style={{ marginLeft: 'auto' }}>
          {users.length} user{users.length === 1 ? '' : 's'}
        </span>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        loading={loading}
        rowKey="enrolled_user_id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) =>
            `Showing ${range[0]}-${range[1]} of ${total} items`,
          position: ['bottomCenter'],
        }}
        scroll={{ x: 1100 }}
      />

      <Modal
        title={editingUser ? 'Edit Participant' : 'Add Participant'}
        visible={isModalVisible}
        onOk={handleModalOk}
        confirmLoading={saving}
        okText={editingUser ? 'Save' : 'Add'}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label="Email"
            extra="Used as the participant's login username."
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input placeholder="participant@example.com" autoComplete="off" />
          </Form.Item>

          <Form.Item name="name" label="Name">
            <Input placeholder="Full name (optional)" />
          </Form.Item>

          <Form.Item
            name="password"
            label={editingUser ? 'Reset Password' : 'Password'}
            extra={editingUser ? 'Leave blank to keep the current password.' : undefined}
            rules={[
              ...(editingUser ? [] : [{ required: true, message: 'Password is required' }]),
              { max: 50, message: 'Password must be at most 50 characters' },
              { whitespace: true, message: 'Password cannot be only spaces' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="programId"
            label="Program"
            extra="Leave empty to keep the participant without an active program."
          >
            <Select
              allowClear
              showSearch
              placeholder="Select a program"
              optionFilterProp="children"
            >
              {programs.map((p) => (
                <Option key={p.program_id} value={p.program_id}>
                  {`${p.program_name || `Program #${p.program_id}`}${p.program_is_active ? '' : ' (inactive)'}`}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EnrolledUsersList;
